/**
 * The notch, the home indicator, and the call surface.
 *
 * Every other screen in this app is inside `Shell`, which applies the safe-area
 * insets once for all of them. The call routes are the documented exception —
 * `Shell` returns a bare `<Outlet/>` for `/call/*` — so nothing has applied
 * them by the time a call screen renders. That is not cosmetic here: the
 * control bar is the bottom-most thing on the screen and it carries the LEAVE
 * button, so an unpadded call puts "hang up" underneath the iOS home-indicator
 * gesture area, where a mis-tap either drops the call or swipes the app away.
 *
 * The tokens themselves (`--sat/--sar/--sab/--sal`) are resolved once in
 * `styles.css` from `env(safe-area-inset-*)`, and they only return anything at
 * all because `index.html` carries `viewport-fit=cover`. They are 0 on every
 * device without a notch, so none of this costs a pixel on a desktop.
 *
 * Why `calc()` strings rather than nesting a padded box inside a padded box:
 * these surfaces are `grid place-items-center` / `flex items-center`, and an
 * extra wrapper between the centring container and its child changes what is
 * being centred. Adding to the design's own padding keeps the layout identical
 * where the insets are 0, which is every case except a phone.
 *
 * LEFT and RIGHT are not decoration. In landscape — a phone held sideways in a
 * call is completely normal — the inset moves from the top to one SIDE, and a
 * top-and-bottom-only fix is exactly the one that misses it.
 */

/** For a surface that has no padding of its own: the raw insets. */
export const SAFE_PAD =
  'pt-[var(--sat)] pr-[var(--sar)] pb-[var(--sab)] pl-[var(--sal)]'

/** `p-6` (1.5rem), plus the insets. The centred message screens. */
export const SAFE_PAD_6 =
  'pt-[calc(1.5rem+var(--sat))] pr-[calc(1.5rem+var(--sar))] pb-[calc(1.5rem+var(--sab))] pl-[calc(1.5rem+var(--sal))]'

/**
 * `px-5 py-4`, rising to `py-10` at `lg` — plus the insets, at every size.
 *
 * The prejoin door. 80px of vertical padding is a ninth of an iPhone SE
 * viewport spent on nothing, and that screen has to hold a camera preview, a
 * name field and a join button without scrolling.
 *
 * The generous padding waits until `lg` rather than `sm` for the landscape
 * reason that runs through this whole feature: a phone on its side is 844px
 * wide and 390px TALL. It is over `sm`, so an `sm:`-keyed 40px top and bottom
 * lands on the one viewport that has 390px of height to spend, and pushed the
 * join button off the bottom of it — measured, at 844x390.
 */
export const SAFE_PAD_DOOR = [
  'pt-[calc(1rem+var(--sat))] pb-[calc(1rem+var(--sab))]',
  'pr-[calc(1.25rem+var(--sar))] pl-[calc(1.25rem+var(--sal))]',
  'lg:pt-[calc(2.5rem+var(--sat))] lg:pb-[calc(2.5rem+var(--sab))]',
].join(' ')
